<template>
  <a-drawer
    :visible="visible"
    :mask-closable="true"
    :destroy-on-close="true"
    placement="right"
    :closable="false"
    :width="480"
    :body-style="{
      padding: '0px',
      marginBottom: '53px'
    }"
    :mask-style="{
      backgroundColor: 'transparent'
    }"
    @update:visible="visible => $emit('update:visible', visible)"
  >
    <div class="container">
      <div class="review-title">
        {{ $t('okr.overallReview.title') }}
      </div>
      <MentionTextarea
        v-model:value="reviewContent.content"
        :mentioned-user-ids="reviewContent.mentionedUserIds"
        :placeholder="$t('okr.review.placeholder')"
        :rows="3"
      />
      <Upload
        ref="upload"
        class="upload"
        :button="{
          name: $t('okr.review.uploadButton'),
          type: 'text',
          size: 'small',
          style: {
            marginTop: '8px',
            backgroundColor: '#F8F9FF'
          }
        }"
        :before-upload="beforeUpload"
        @files-uploaded="onAttachmentsUploaded"
      />
      <FileList
        :can-edit="true"
        :files="reviewAttachments"
        :style="{ marginTop: '8px' }"
        @file-deleted="onAttachmentDeleted"
      />
    </div>
    <template #title>
      <div
        class="drawer-title"
      >
        <IconFont
          type="iconfupanzongjie"
          class="icon"
        />
        {{ review.reviewFlag + $t('okr.titles.review') }}
      </div>
    </template>
    <div class="drawer-form-action">
      <a-button
        style="margin-right: 8px"
        @click="$emit('update:visible', false)"
      >
        {{ $t('shared.cancel') }}
      </a-button>
      <a-button
        type="primary"
        :loading="loading"
        @click="save"
      >
        {{ $t('shared.editDialog.ok') }}
      </a-button>
    </div>
  </a-drawer>
</template>
<script>
// import map from 'lodash/map'
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'

import { MAX_FILE_SIZE, MAX_FILE_NUM } from '@/constants/file'
import { replaceHtml2Str, replaceStr2Html } from '@/utils/line-break'
import { updateOverallReview as updateReview, createOverallReview as createReview } from '@/api/okr'
import MentionTextarea from '@/components/MentionTextarea'
import Upload from '@/components/Upload'
import FileList from '@/components/FileList'

const maxFileSizeMB = MAX_FILE_SIZE / 1024 / 1024

export default {
  components: {
    MentionTextarea,
    Upload,
    FileList
  },
  props: {
    relatedId: {
      type: Number,
      required: true
    },
    visible: {
      type: Boolean,
      required: true
    },
    review: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'update:visible',
    'review-saved'
  ],
  setup (props, { emit }) {
    const upload = ref(null)
    const { t } = useI18n()
    const reviewContent = ref({ content: '', mentionedUserIds: [] })
    // const hasChanged = ref(false)
    watch(() => props.review, (val) => {
      reviewContent.value = {
        content: props.review.reviewContent
          ? replaceHtml2Str(props.review.reviewContent)
          : '',
        mentionedUserIds: props.review.reviewContentMentionUids
          ? props.review.reviewContentMentionUids.split(',')
          : []
      }
    })

    const reviewAttachments = computed(() => {
      return JSON.parse(props.review.reviewAttachments || '[]')
    })

    function beforeUpload (files) {
      if (files.length > 5) {
        message.warning(t('okr.review.uploadMaxNumWarning', { maxFileNum: MAX_FILE_NUM }))
        return false
      }

      let hasOverflowFile = false

      files.forEach(file => {
        if (file.size > MAX_FILE_SIZE) {
          hasOverflowFile = true
          message.warning(t('okr.review.uploadMaxSizeWarning', {
            filename: file.name,
            maxFileSize: maxFileSizeMB
          }))
        }
      })

      if (hasOverflowFile) {
        return false
      }

      return true
    }

    function onAttachmentsUploaded (newAttachments) {
      const attachments = [
        ...reviewAttachments.value,
        ...newAttachments.map(item => ({
          id: item.id,
          name: item.filename
        }))
      ]
      const reviewId = props.review.id
      const relatedId = props.relatedId
      const params = {
        okrlist_id: ('id' in props.review) ? undefined : relatedId,
        review_content: replaceStr2Html(reviewContent.value.content),
        review_content_mention_uids: reviewContent.value.mentionedUserIds.join(','),
        review_attachments: JSON.stringify(attachments),
        review_flag: props.review.reviewFlag
      }
      const p = reviewId ? updateReview(reviewId, params) : createReview(params)
      p.then(() => {
        upload.value.reset()
        emit('review-saved')
      })
    }

    function onAttachmentDeleted (attachmentId) {
      const attachments = reviewAttachments.value
        .filter(attachment => attachment.id !== attachmentId)
      updateReview(props.review.id, {
        review_attachments: JSON.stringify(attachments),
        review_flag: props.review.reviewFlag
      }).then(() => emit('review-saved'))
    }

    const loading = ref(false)
    function save () {
      loading.value = true
      const reviewId = props.review.id
      const relatedId = props.relatedId
      const params = {
        okrlist_id: ('id' in props.review) ? undefined : relatedId,
        review_content: replaceStr2Html(reviewContent.value.content),
        review_content_mention_uids: reviewContent.value.mentionedUserIds.join(','),
        review_flag: props.review.reviewFlag
      }
      const p = reviewId ? updateReview(reviewId, params) : createReview(params)
      p.then(() => {
        loading.value = false
        emit('update:visible', false)
        emit('review-saved')
        message.success(t('okr.review.saveSuccessMessage'))
      })
    }

    return {
      upload,
      reviewContent,
      beforeUpload,
      onAttachmentsUploaded,
      onAttachmentDeleted,
      save,
      reviewAttachments,
      loading
    }
  }
}
</script>
<style lang="less" scoped>
.drawer-title {
  display: flex;
  align-items: center;
  line-height: 32px;
}
.container {
  padding: 0 16px;
}

.content {
  margin-top: 24px;
}

.icon {
  margin-right: 8px;
}

.review-title {
  font-weight: bold;
  margin-top: 24px;
  margin-bottom: 12px;
}

.upload {
  margin-top: 24px;
}

.tip {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.56);
  margin: 8px 0;
}
</style>
